import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ExportInterfaceConfigurationType, ExportInterfaceFieldAssignmentDTO, ExportInterfaceFieldAssignmentTemplateDTO, IUpdateExportInterfaceFieldAssignmentTemplateDTO, UpdateExportInterfaceFieldAssignmentTemplateDTO } from 'src/app/base/models/models.swagger.generated';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { FieldAssignmentTemplatesOverviewComponent } from '../shared/field-assignment-templates-overview/field-assignment-templates-overview.component';
import { FieldAssignmentTemplatesOverviewService } from '../shared/field-assignment-templates-overview/field-assignment-templates-overview.service';
import { merge, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

import { FieldAssignmentTemplateFormComponent } from './field-assignment-template-form/field-assignment-template-form.component';
import { TitleBarComponent } from '../../../base/shared/title-bar/title-bar.component';
import { NavigationEnd, Router } from '@angular/router';
import { fieldAssignmentTemplatesExpConfRoute } from 'src/app/core/routing/route-names';
import * as pages from '../../../core/routing/page-names';
import { UrlHelper } from 'src/app/base/shared/url-helper';
import { ToastService } from 'src/app/base/services/toast.service';
import { FieldAssignmentTemplateFormType } from './field-assignment-template-form/field-assignment-template-form-type';

@Component({
    selector: 'app-field-assignment-templates',
    templateUrl: './field-assignment-templates.component.html',
    styleUrls: ['./field-assignment-templates.component.scss'],
    imports: [TitleBarComponent, FieldAssignmentTemplatesOverviewComponent, FieldAssignmentTemplateFormComponent, TranslateModule]
})
export class FieldAssignmentTemplatesComponent implements OnInit, OnDestroy {

  @ViewChild(FieldAssignmentTemplatesOverviewComponent) templateOverviewComponent: FieldAssignmentTemplatesOverviewComponent;

  public template: ExportInterfaceFieldAssignmentTemplateDTO;
  public templateForm: FormGroup<FieldAssignmentTemplateFormType>;

  public selectedTemplateId : number;

  private _subscription: Subscription = new Subscription();

  constructor(
    private templateService: FieldAssignmentTemplatesOverviewService,
    private fb: FormBuilder,
    private modalService: ModalService,
    private router: Router,
    private toastService: ToastService,
  ) { }

  ngOnInit() {
    this.initForm();

    this.router.events.pipe(filter(event => event instanceof NavigationEnd && event.url.includes(fieldAssignmentTemplatesExpConfRoute))).subscribe(() => {
      this.urlChanged();
    });

    // call the first time urlChanged on our own
    this.urlChanged();
  }

  public urlChanged(): void {
    var id = UrlHelper.getIdForSegment(pages.fieldAssignmentTemplatesPageName, this.router)
    if (id != null){
      this.selectedTemplateId = id;
    } else {
      this.selectedTemplateId = null;
    }
  }

  public addTemplate() {
    this.templateService.addRecord().subscribe((data: ExportInterfaceFieldAssignmentTemplateDTO) => {
      this.templateOverviewComponent.recordAdded(data.id);
    });
  }

  public closeTemplate() {
    this.templateOverviewComponent.resetRowSelected();
    this.template = null;
    this.templateForm.reset();
    this.templateForm.markAsPristine();
    this.router.navigate([fieldAssignmentTemplatesExpConfRoute]);
  }

  public deleteTemplate() {
    const dialog = this.modalService.showOkCancelDialog('i18n.domain.export-configuration.field-assignment-templates.delete-dialog.title', 'i18n.domain.export-configuration.field-assignment-templates.delete-dialog.question');
    dialog.onOkSubject.subscribe(() => {
      this.templateService.deleteRecord(this.template.id).subscribe(() => {
        this.templateOverviewComponent.refresh();
      });
      this.closeTemplate();
    });
  }

  public saveTemplate() {
    this.templateForm.markAllAsTouched();
    if (this.templateForm.invalid) {
      this.toastService.showWarning('i18n.domain.export-configuration.configurations.messages.form-invalid.title',
        'i18n.domain.export-configuration.configurations.messages.form-invalid.message', null);
      return;
    }
    const templateValues = this.templateForm.getRawValue() as IUpdateExportInterfaceFieldAssignmentTemplateDTO;
    templateValues.id = this.template.id;
    const updateData = new UpdateExportInterfaceFieldAssignmentTemplateDTO({
      id: this.template.id,
      templateName: templateValues.templateName,
      configurationType: templateValues.configurationType,
      assignments: templateValues.assignments.map((a, index) => {
        a.position = index;
        return new ExportInterfaceFieldAssignmentDTO(a)
      })
    });
    this.templateService.updateRecord(updateData).subscribe({next: () => {
      this.closeTemplate();
      this.templateOverviewComponent.refresh();
    }, error: (error) => {
      // we just catch since warnings are handled in service
    }});
  }

  public templateSelected(selectedTemplate: ExportInterfaceFieldAssignmentTemplateDTO) {
    this.template = selectedTemplate;
    this.templateService.configurationType = selectedTemplate.configurationType;
    this.loadDataToForm(selectedTemplate);
    if (selectedTemplate?.id != null) {
      this.router.navigate([fieldAssignmentTemplatesExpConfRoute + "/" + selectedTemplate.id]);
    } else {
      this.router.navigate([fieldAssignmentTemplatesExpConfRoute]);
    }
  }

  private initForm() {
    this.templateForm = this.fb.group({
      templateName: this.fb.control<string>(null, Validators.required),
      configurationType: this.fb.control<ExportInterfaceConfigurationType>(null, Validators.required),
      assignments: this.fb.control<ExportInterfaceFieldAssignmentDTO[]>(null),
    });
  }

  private loadDataToForm(data: ExportInterfaceFieldAssignmentTemplateDTO) {
    this.templateForm = this.fb.group({
      templateName: this.fb.control<string>(data.templateName, Validators.required),
      configurationType: this.fb.control<ExportInterfaceConfigurationType>(data.configurationType, Validators.required),
      assignments: this.fb.control<ExportInterfaceFieldAssignmentDTO[]>(data.assignments)
    });
    this.templateService.configurationType = data.configurationType;
    this._subscription.add(this.templateForm.controls.configurationType.valueChanges
      .subscribe((value) => {
        this.templateService.configurationType = value;
      }),
    );
    if (data.assignments?.length > 0) {
      this.templateForm.controls.configurationType.disable();
    }
    else {
      this.templateForm.controls.configurationType.enable();
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.templateForm.dirty) {
      const dialog = this.modalService.showOkCancelDialog('i18n.domain.export-configuration.field-assignment-templates.discard-dialog.title', 'i18n.domain.export-configuration.field-assignment-templates.discard-dialog.question');
      return merge(
        dialog.onOkSubject.pipe(map(() => true)),
        dialog.onCancelSubject.pipe(map(() => false)),
      );
    }
    return true;
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }
}
